import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, KeyRound } from 'lucide-react';

const playFlipSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const len = ctx.sampleRate * 0.12;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 4) * 0.08;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filt = ctx.createBiquadFilter();
    filt.type = 'highpass';
    filt.frequency.value = 2200;
    src.connect(filt);
    filt.connect(ctx.destination);
    src.start();
  } catch (e) { /* silent */ }
};

const playDoorSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(100, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(350, ctx.currentTime + 0.7);
    gain.gain.setValueAtTime(0.04, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.9);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.9);
  } catch (e) { /* silent */ }
};

export const DoorOpening = ({ onComplete }) => {
  const [phase, setPhase] = useState('closed');

  useEffect(() => {
    const t1 = setTimeout(() => { setPhase('opening'); playDoorSound(); }, 900);
    const t2 = setTimeout(() => { setPhase('revealing'); }, 2500);
    const t3 = setTimeout(() => { onComplete(); }, 3100);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onComplete]);

  return (
    <div
      className="fixed inset-0 z-[10000]"
      style={{
        opacity: phase === 'revealing' ? 0 : 1,
        transition: 'opacity 0.6s ease-out',
        pointerEvents: phase === 'revealing' ? 'none' : 'auto',
      }}
      data-testid="door-opening"
    >
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at center, #F0FFF4 0%, #DCFCE7 40%, #F8FAFC 100%)',
      }} />

      {/* Left Door */}
      <div className="absolute top-0 left-0 w-1/2 h-full" style={{ perspective: '1200px', perspectiveOrigin: 'left center' }}>
        <div className="w-full h-full relative" style={{
          transformOrigin: 'left center',
          transform: phase === 'opening' || phase === 'revealing' ? 'rotateY(-100deg)' : 'rotateY(0deg)',
          transition: 'transform 1.4s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: '6px 0 40px rgba(0,0,0,0.3)',
          background: 'linear-gradient(135deg, #4ADE80 0%, #22C55E 50%, #16A34A 100%)',
        }}>
          <div className="absolute inset-5 border border-white/20" />
          <div className="absolute inset-10 border border-white/10" />
          <div className="absolute right-5 top-1/2 -translate-y-1/2">
            <div className="w-3 h-20 bg-gradient-to-b from-[#FDE68A] to-[#F59E0B] rounded-full" style={{ boxShadow: '0 0 12px rgba(245,158,11,0.5)' }} />
          </div>
        </div>
      </div>

      {/* Right Door */}
      <div className="absolute top-0 right-0 w-1/2 h-full" style={{ perspective: '1200px', perspectiveOrigin: 'right center' }}>
        <div className="w-full h-full relative" style={{
          transformOrigin: 'right center',
          transform: phase === 'opening' || phase === 'revealing' ? 'rotateY(100deg)' : 'rotateY(0deg)',
          transition: 'transform 1.4s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: '-6px 0 40px rgba(0,0,0,0.3)',
          background: 'linear-gradient(225deg, #4ADE80 0%, #22C55E 50%, #16A34A 100%)',
        }}>
          <div className="absolute inset-5 border border-white/20" />
          <div className="absolute inset-10 border border-white/10" />
          <div className="absolute left-5 top-1/2 -translate-y-1/2">
            <div className="w-3 h-20 bg-gradient-to-b from-[#FDE68A] to-[#F59E0B] rounded-full" style={{ boxShadow: '0 0 12px rgba(245,158,11,0.5)' }} />
          </div>
        </div>
      </div>

      {/* Center Logo */}
      {phase === 'closed' && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="text-center animate-pulse">
            <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl text-white font-bold tracking-tight" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
              SHOPPERDEN
            </h1>
            <p className="text-white/90 text-sm tracking-[0.3em] uppercase mt-3 font-semibold" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.3)' }}>
              Opening doors to your future
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export const BookLayout = ({ pages, pageLabels }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [flipping, setFlipping] = useState(false);
  const [flipDir, setFlipDir] = useState('');
  const containerRef = useRef(null);
  const touchX = useRef(0);
  const total = pages.length;

  const goTo = useCallback((i) => {
    if (i < 0 || i >= total || flipping) return;
    const dir = i > currentPage ? 'next' : 'prev';
    setFlipDir(dir);
    setFlipping(true);
    playFlipSound();
    setTimeout(() => {
      setCurrentPage(i);
      setFlipping(false);
      setFlipDir('');
      if (containerRef.current) {
        window.scrollTo({ top: containerRef.current.offsetTop - 80, behavior: 'smooth' });
      }
    }, 450);
  }, [currentPage, total, flipping]);

  const next = useCallback(() => goTo(currentPage + 1), [currentPage, goTo]);
  const prev = useCallback(() => goTo(currentPage - 1), [currentPage, goTo]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [next, prev]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const diff = touchX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 60) { diff > 0 ? next() : prev(); }
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Sticky page navigation */}
      <div className="sticky top-[72px] z-40 bg-white/95 backdrop-blur-md border-b border-slate-200/60 py-2 px-4 shadow-sm" data-testid="book-nav">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={prev}
            disabled={currentPage === 0}
            className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${currentPage === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100 hover:text-[#E07A3A]'}`}
            data-testid="book-prev"
          >
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>

          {/* Desktop tabs with key icons */}
          <div className="hidden md:flex items-center gap-1">
            {pageLabels.map((label, i) => (
              <button
                key={label}
                onClick={() => goTo(i)}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase transition-all ${i === currentPage
                  ? 'bg-[#E07A3A] text-white shadow-sm shadow-[#E07A3A]/20'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`}
                data-testid={`book-tab-${i}`}
              >
                <KeyRound className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>

          {/* Mobile: key icons instead of dots */}
          <div className="flex md:hidden items-center gap-1.5">
            {pageLabels.map((_, i) => (
              <button
                key={`mobile-${i}`}
                onClick={() => goTo(i)}
                className={`p-1.5 rounded-full transition-all ${i === currentPage
                  ? 'bg-[#E07A3A] text-white scale-110'
                  : 'bg-slate-100 text-slate-300'}`}
                data-testid={`book-dot-${i}`}
              >
                <KeyRound className="h-3.5 w-3.5" />
              </button>
            ))}
          </div>

          <button
            onClick={next}
            disabled={currentPage === total - 1}
            className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${currentPage === total - 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100 hover:text-[#E07A3A]'}`}
            data-testid="book-next"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="text-center mt-0.5">
          <span className="text-[10px] text-slate-300 tracking-widest uppercase font-medium">
            Page {currentPage + 1} of {total}
          </span>
        </div>
      </div>

      {/* 3D Page Content */}
      <div
        className="min-h-[80vh] overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="book-content"
      >
        <div style={{ perspective: '2000px' }}>
          <div
            style={{
              transformOrigin: flipping
                ? (flipDir === 'next' ? 'left center' : 'right center')
                : 'center center',
              transform: flipping
                ? `rotateY(${flipDir === 'next' ? '-14' : '14'}deg) scale(0.96)`
                : 'rotateY(0deg) scale(1)',
              opacity: flipping ? 0.25 : 1,
              transition: 'all 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
              willChange: 'transform, opacity',
            }}
          >
            {pages[currentPage]}
          </div>
        </div>
      </div>

      {/* Mobile bottom nav with key icon */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white/95 backdrop-blur-md border-t border-slate-200 px-4 py-2 flex items-center justify-between"
        style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}
      >
        <button
          onClick={prev}
          disabled={currentPage === 0}
          className={`flex items-center gap-1 px-4 py-2.5 rounded-lg text-sm font-semibold ${currentPage === 0 ? 'text-slate-300' : 'text-slate-700 bg-slate-100'}`}
          data-testid="book-prev-mobile"
        >
          <ChevronLeft className="h-4 w-4" /> Prev
        </button>
        <span className="flex items-center gap-1.5 text-xs text-slate-500 font-semibold">
          <KeyRound className="h-4 w-4 text-[#E07A3A]" />
          {pageLabels[currentPage]}
        </span>
        <button
          onClick={next}
          disabled={currentPage === total - 1}
          className={`flex items-center gap-1 px-4 py-2.5 rounded-lg text-sm font-semibold ${currentPage === total - 1 ? 'text-slate-300' : 'text-white bg-[#E07A3A]'}`}
          data-testid="book-next-mobile"
        >
          Next <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
